import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH } from '../config/game';
import { RENDER_SCALE } from '../config/rendering';
import { PLAYER_TEMPLATES, PlayerTemplateDefinition } from '../systems/PlayerTemplate';
import { ts } from '../utils/textStyle';
import { DevTuningMenu } from '../systems/DevTuningMenu';
import changelogRaw from '../../CHANGELOG.md?raw';
import { applyTunablePreset, resetTunables } from '../config/tunables';


const VERSION = import.meta.env.VITE_APP_VERSION ?? '0.0.0';
const BUILD_TIME = import.meta.env.VITE_BUILD_TIME ?? 'dev';

export class BootScene extends Phaser.Scene {
  private statusText?: Phaser.GameObjects.Text;
  private devTuningMenu?: DevTuningMenu;

  constructor() {
    super('boot');
  }

  create() {
    const width = GAME_WIDTH;
    const height = GAME_HEIGHT;

    this.cameras.main.setBackgroundColor('#17140f');
    this.cameras.main.setOrigin(0, 0);
    this.cameras.main.setZoom(RENDER_SCALE);
    this.cameras.main.setScroll(0, 0);
    this.devTuningMenu = new DevTuningMenu();
    this.devTuningMenu.mount();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.devTuningMenu?.destroy());

    const centerX = width / 2;
    const centerY = height / 2;

    this.add.rectangle(centerX, centerY, width, height, 0x17140f);

    const changelogBtn = this.add.rectangle(width - 60, 40, 90, 32, 0x241f19, 1);
    changelogBtn.setStrokeStyle(1, 0x6d5c38, 1);
    const changelogText = this.add.text(width - 60, 40, '更新日志', ts({
      fontFamily: 'Arial, sans-serif',
      fontSize: '13px',
      color: '#e0b15a',
      fontStyle: 'bold'
    })).setOrigin(0.5);
    
    changelogBtn.setInteractive({ useHandCursor: true });
    changelogText.setInteractive({ useHandCursor: true });
    
    const openLog = () => this.showChangelog();
    changelogBtn.on('pointerdown', openLog);
    changelogText.on('pointerdown', openLog);

    this.add
      .text(centerX, Math.max(44, height * 0.12), 'Anime Brotato', ts({
        fontFamily: 'Georgia, "Times New Roman", serif',
        fontSize: '34px',
        color: '#f4e8cf',
        align: 'center',
      }))
      .setOrigin(0.5);

    this.add
      .text(centerX, Math.max(88, height * 0.12 + 40), 'v0.1 prototype', ts({
        fontFamily: 'Arial, sans-serif',
        fontSize: '14px',
        color: '#c9b58b',
        align: 'center',
      }))
      .setOrigin(0.5);

    const arenaTop = Math.max(118, height * 0.2);
    const arenaHeight = Math.min(260, height * 0.31);
    const arenaWidth = Math.min(width * 0.82, 320);
    const arena = this.add.rectangle(centerX, arenaTop + arenaHeight / 2, arenaWidth, arenaHeight, 0x241f19, 1);
    arena.setStrokeStyle(2, 0x6d5c38, 0.72);

    this.add.circle(centerX, arena.y, 18, 0xf0c86a, 1);
    this.add.circle(centerX - 52, arena.y + 30, 10, 0x79b8ff, 0.86);
    this.add.circle(centerX + 46, arena.y - 36, 12, 0xff7f6e, 0.82);
    this.add.circle(centerX + 24, arena.y + 58, 8, 0x8ee89e, 0.82);

    this.add
      .text(centerX, arena.y + arenaHeight / 2 - 26, '选择开局模板', ts({
        fontFamily: 'Arial, sans-serif',
        fontSize: '13px',
        color: '#9d8b68',
        align: 'center',
      }))
      .setOrigin(0.5);

    this.renderTemplateCards(centerX, Math.min(height - 244, arenaTop + arenaHeight + 68));

    this.statusText = this.add
      .text(centerX, height - 112, '选定后进入战斗', ts({
        fontFamily: 'Arial, sans-serif',
        fontSize: '13px',
        color: '#d9cba8',
        align: 'center',
      }))
      .setOrigin(0.5);

    const meta = `version ${VERSION}\nbuilt ${BUILD_TIME}`;
    this.add
      .text(centerX, height - 72, meta, ts({
        fontFamily: 'Arial, sans-serif',
        fontSize: '11px',
        color: '#8f7f63',
        align: 'center',
        lineSpacing: 4,
      }))
      .setOrigin(0.5);

    this.maybeStartAutoplay();
  }

  private maybeStartAutoplay() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('autoplay') !== '1') {
      return;
    }

    resetTunables();
    const preset = params.get('preset');
    if (preset) {
      applyTunablePreset(preset);
    }

    const templateId = params.get('template') ?? 'agile';
    const template = PLAYER_TEMPLATES.find((item) => item.id === templateId) ?? PLAYER_TEMPLATES[0];
    this.time.delayedCall(80, () => this.startGame(template));
  }

  private showChangelog() {
    const overlay = document.createElement('div');
    overlay.style.position = 'absolute';
    overlay.style.top = '0';
    overlay.style.left = '0';
    overlay.style.width = '100%';
    overlay.style.height = '100%';
    overlay.style.backgroundColor = 'rgba(10, 8, 5, 0.85)';
    overlay.style.backdropFilter = 'blur(6px)';
    overlay.style.zIndex = '9999';
    overlay.style.display = 'flex';
    overlay.style.flexDirection = 'column';
    overlay.style.alignItems = 'center';
    overlay.style.justifyContent = 'center';
    overlay.style.padding = '40px 20px';
    overlay.style.boxSizing = 'border-box';
    
    // Add close interaction on the backdrop
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        overlay.remove();
      }
    });

    const modal = document.createElement('div');
    modal.style.width = '100%';
    modal.style.maxWidth = '460px';
    modal.style.maxHeight = '100%';
    modal.style.backgroundColor = '#1e1913';
    modal.style.border = '2px solid #e0b15a';
    modal.style.borderRadius = '12px';
    modal.style.display = 'flex';
    modal.style.flexDirection = 'column';
    modal.style.boxShadow = '0 10px 40px rgba(0,0,0,0.8)';
    modal.style.overflow = 'hidden';

    const header = document.createElement('div');
    header.style.padding = '16px 20px';
    header.style.borderBottom = '1px solid #4a3f31';
    header.style.display = 'flex';
    header.style.justifyContent = 'space-between';
    header.style.alignItems = 'center';
    header.style.backgroundColor = '#29221a';

    const title = document.createElement('div');
    title.textContent = '📜 更新日志 (Changelog)';
    title.style.color = '#f5e5c8';
    title.style.fontSize = '18px';
    title.style.fontWeight = 'bold';

    const closeBtn = document.createElement('button');
    closeBtn.textContent = '✕';
    closeBtn.style.background = 'none';
    closeBtn.style.border = 'none';
    closeBtn.style.color = '#e0b15a';
    closeBtn.style.fontSize = '20px';
    closeBtn.style.cursor = 'pointer';
    closeBtn.addEventListener('click', () => overlay.remove());

    header.append(title, closeBtn);

    const contentArea = document.createElement('div');
    contentArea.style.padding = '20px';
    contentArea.style.overflowY = 'auto';
    contentArea.style.flex = '1';

    const pre = document.createElement('pre');
    pre.textContent = changelogRaw;
    pre.style.color = '#d9cba8';
    pre.style.fontFamily = 'monospace';
    pre.style.fontSize = '12px';
    pre.style.whiteSpace = 'pre-wrap';
    pre.style.wordWrap = 'break-word';
    pre.style.lineHeight = '1.6';
    pre.style.margin = '0';

    contentArea.appendChild(pre);
    modal.append(header, contentArea);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);
  }

  private renderTemplateCards(centerX: number, startY: number) {
    PLAYER_TEMPLATES.forEach((template, index) => {
      const y = startY + index * 82;
      const card = this.add.rectangle(centerX, y, 326, 68, 0x241f19, 1);
      card.setStrokeStyle(2, template.color, 0.86);
      card.setInteractive({ useHandCursor: true });

      const marker = this.add.circle(centerX - 138, y, 13, template.color, 1);
      marker.setStrokeStyle(2, template.strokeColor, 0.9);

      const name = this.add
        .text(centerX - 112, y - 20, template.name, ts({
          fontFamily: 'Arial, sans-serif',
          fontSize: '18px',
          color: '#f5e5c8',
          fontStyle: 'bold',
        }));

      const summary = this.add
        .text(centerX - 112, y + 2, `${template.summary}  /  ${template.weakness}`, ts({
          fontFamily: 'Arial, sans-serif',
          fontSize: '12px',
          color: '#d9cba8',
        }));

      const subtitle = this.add
        .text(centerX - 112, y + 21, template.subtitle, ts({
          fontFamily: 'Arial, sans-serif',
          fontSize: '11px',
          color: '#9d8b68',
        }));

      const start = () => this.startGame(template);
      card.on('pointerdown', start);
      marker.setInteractive({ useHandCursor: true }).on('pointerdown', start);
      name.setInteractive({ useHandCursor: true }).on('pointerdown', start);
      summary.setInteractive({ useHandCursor: true }).on('pointerdown', start);
      subtitle.setInteractive({ useHandCursor: true }).on('pointerdown', start);

      card.on('pointerover', () => card.setFillStyle(0x2d261d, 1));
      card.on('pointerout', () => card.setFillStyle(0x241f19, 1));
    });
  }

  private startGame(template: PlayerTemplateDefinition) {
    this.statusText?.setText(`进入 ${template.name}`);
    this.devTuningMenu?.destroy();
    this.scene.start('game', { templateId: template.id });
  }
}
